import React, { useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import { ContactFormData } from '../services/contactsService';
import ContactForm, { ContactFormValues, Contact } from '../components/common/ContactForm';
import { Page, PageHead, PageTitle, PageSub } from '../components/ui/Page';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, InputGroup, InputAddon } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Callout } from '../components/ui/Callout';
import { Avatar } from '../components/ui/Avatar';
import { Icon } from '../components/ui/Icon';
import { Dialog } from '../components/ui/radix/Dialog';
import { AlertDialog } from '../components/ui/radix/AlertDialog';

type Tab = 'all' | 'favorites';

const initials = (name: string) =>
  name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const ContactsPage: React.FC = () => {
  const {
    contacts,
    isLoading,
    isError,
    error,
    refetch,
    addContact,
    updateContact,
    deleteContact,
    toggleFavorite,
    setAsDefault,
    isPendingAdd,
    isPendingUpdate,
  } = useContacts({});

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<Tab>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentContactId, setCurrentContactId] = useState<string | null>(null);
  const [contactFormData, setContactFormData] = useState<Partial<Contact> | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<{ id: string; name: string } | null>(
    null
  );
  const [toast, setToast] = useState<{ tone: 'ok' | 'warn'; message: string } | null>(null);

  const showToast = (tone: 'ok' | 'warn', message: string) => {
    setToast({ tone, message });
    setTimeout(() => setToast(null), 3500);
  };

  const allContacts: Contact[] = (Array.isArray(contacts)
    ? contacts
    : (contacts as any)?.data || []) as Contact[];

  const filtered = allContacts.filter((c: any) => {
    const q = search.toLowerCase();
    const matchSearch =
      c.nickname?.toLowerCase().includes(q) ||
      c.contactName?.toLowerCase().includes(q) ||
      c.companyName?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.phone?.toLowerCase().includes(q) ||
      c.street?.toLowerCase().includes(q) ||
      c.zipCode?.toLowerCase().includes(q);
    if (tab === 'all') return matchSearch;
    return matchSearch && c.isFavorite;
  });

  const handleOpenAdd = () => {
    setIsEditMode(false);
    setCurrentContactId(null);
    setContactFormData(null);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = (contactId: string) => {
    const c = allContacts.find((x: any) => x.id === contactId);
    if (!c) return;
    setIsEditMode(true);
    setCurrentContactId(contactId);
    setContactFormData(c);
    setFormError(null);
    setDialogOpen(true);
  };

  const onSubmitContact = async (data: ContactFormValues) => {
    setFormError(null);
    try {
      const payload: ContactFormData = {
        nickname: data.nickname,
        contactName: data.contactName,
        companyName: data.companyName || '',
        email: data.email,
        phone: data.phone,
        street: data.street,
        houseNo: data.houseNo || '',
        zipCode: data.zipCode || '',
        deliveryDetails: data.deliveryDetails || '',
        isFavorite: data.isFavorite || false,
        isDefault: data.isDefault || false,
        country_id: data.country_id || null,
        state_id: data.state_id || null,
        city_id: data.city_id || null,
        city_other: data.city_other || '',
      };
      if (isEditMode && currentContactId) {
        await updateContact({ id: currentContactId, data: payload });
        showToast('ok', 'Contact updated');
      } else {
        await addContact(payload);
        showToast('ok', 'Contact created');
      }
      setDialogOpen(false);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Failed to save contact';
      setFormError(msg);
      showToast('warn', msg);
    }
  };

  const askDelete = (id: string, name: string) => {
    setContactToDelete({ id, name });
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;
    try {
      await deleteContact(contactToDelete.id);
      showToast('ok', 'Contact deleted');
    } catch {
      showToast('warn', 'Error deleting contact');
    } finally {
      setDeleteOpen(false);
      setContactToDelete(null);
    }
  };

  return (
    <Page width="wide">
      <PageHead>
        <div>
          <PageTitle>Contacts</PageTitle>
          <PageSub>Save recipients for faster booking.</PageSub>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button>
            <Icon name="upload" size={14} />
            <span>Import CSV</span>
          </Button>
          <Button variant="blue" onClick={handleOpenAdd}>
            <Icon name="plus" size={14} />
            <span>New contact</span>
          </Button>
        </div>
      </PageHead>

      {toast && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone={toast.tone}>
            <Icon name={toast.tone === 'ok' ? 'checkCircle' : 'alertCircle'} size={16} />
            <span>{toast.message}</span>
          </Callout>
        </div>
      )}

      <Card>
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div className="tabs" style={{ border: 'none' }}>
            <button
              type="button"
              className={`tab ${tab === 'all' ? 'active' : ''}`}
              onClick={() => setTab('all')}
            >
              All contacts
            </button>
            <button
              type="button"
              className={`tab ${tab === 'favorites' ? 'active' : ''}`}
              onClick={() => setTab('favorites')}
            >
              Favorites
            </button>
          </div>
          <InputGroup style={{ maxWidth: 320 }}>
            <InputAddon>
              <Icon name="search" size={13} />
            </InputAddon>
            <Input
              placeholder="Search by name, company, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        </div>

        {isError && (
          <div style={{ padding: 16 }}>
            <Callout tone="warn">
              <Icon name="alertCircle" size={16} />
              <span>
                Error loading contacts: {error instanceof Error ? error.message : 'Unknown error'}.
              </span>
              <button
                type="button"
                className="btn btn-ghost btn-xs"
                onClick={() => refetch()}
                style={{ marginInlineStart: 'auto' }}
              >
                Retry
              </button>
            </Callout>
          </div>
        )}

        <table className="table">
          <thead>
            <tr>
              <th>Contact</th>
              <th>Company</th>
              <th>Location</th>
              <th>Phone</th>
              <th></th>
              <th style={{ textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6}>
                  <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    Loading contacts…
                  </div>
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                    {tab === 'favorites' ? 'No favorite contacts.' : 'No contacts found.'}
                  </div>
                </td>
              </tr>
            )}
            {!isLoading &&
              filtered.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Avatar>{initials(c.contactName || c.nickname || 'U')}</Avatar>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, display: 'flex', gap: 6, alignItems: 'center' }}>
                          {c.contactName}
                          {c.isDefault && <Badge tone="ok">Default</Badge>}
                        </div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {c.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{c.companyName || <span className="muted">—</span>}</td>
                  <td>
                    <div style={{ fontSize: 13 }}>
                      {[c.city?.name || c.city_other, c.country?.name].filter(Boolean).join(', ')}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {c.street} {c.houseNo}
                    </div>
                  </td>
                  <td className="muted" style={{ fontSize: 13 }}>
                    {c.phone}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs"
                      onClick={() => toggleFavorite(c.id)}
                      title={c.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                      aria-label={c.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Icon
                        name="star"
                        size={14}
                        color={c.isFavorite ? 'var(--brand-amber)' : 'var(--ink-4)'}
                      />
                    </button>
                  </td>
                  <td style={{ width: 180, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 4 }}>
                      <Button size="xs" variant="ghost" onClick={() => handleOpenEdit(c.id)}>
                        <Icon name="edit" size={13} />
                      </Button>
                      {!c.isDefault && (
                        <Button
                          size="xs"
                          variant="ghost"
                          onClick={() => setAsDefault(c.id)}
                          title="Set as default"
                        >
                          <Icon name="mapPin" size={13} />
                        </Button>
                      )}
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => askDelete(c.id, c.contactName)}
                        title="Delete"
                      >
                        <Icon name="trash" size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={isEditMode ? 'Edit contact' : 'Add new contact'}
        description={
          isEditMode
            ? 'Update contact information and address details.'
            : 'Fill in the details to create a new shipping contact.'
        }
        maxWidth={900}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDialogOpen(false)}
              disabled={isPendingAdd || isPendingUpdate}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="blue"
              form="contact-form"
              disabled={isPendingAdd || isPendingUpdate}
            >
              <Icon name={isEditMode ? 'check' : 'plus'} size={14} />
              <span>
                {isPendingAdd || isPendingUpdate
                  ? 'Saving…'
                  : isEditMode
                    ? 'Save changes'
                    : 'Create contact'}
              </span>
            </Button>
          </div>
        }
      >
        <ContactForm
          isEditMode={isEditMode}
          initialData={contactFormData || undefined}
          onSubmit={onSubmitContact}
          isSubmitting={isPendingAdd || isPendingUpdate}
          formError={formError}
          isRTL={false}
        />
      </Dialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete contact"
        description={
          contactToDelete
            ? `Delete contact "${contactToDelete.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        destructive
      />
    </Page>
  );
};

export default ContactsPage;
